import { CantileverButton } from 'components/CantileverButton'
import SearchBar from 'components/SearchBar'
import { TARGET_METHOD_KEYS } from 'features/sentinels/types'
import { useSentinelDrivers } from 'features/sentinels/useSentinelDrivers'
import {
  SentinelSummary,
  useSentinelList,
} from 'features/sentinels/useSentinelList'
import { useSentinelScores } from 'features/timeSeries/hooks/useSentinelScores'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import { Loader2, X } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import styled, { keyframes } from 'styled-components'

const METHOD_LABELS: Record<string, string> = {
  target_mlp_none: 'MLP (no drivers)',
  target_mlp_driver: 'MLP + drivers',
  target_mlp_driver_own: 'MLP + drivers + own',
  target_knn: 'kNN',
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`

const SearchContainer = styled.div`
  padding: 0.5rem;
  flex-shrink: 0;
`

const List = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0 0.5rem 0.5rem;
`

const Row = styled.div`
  border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
  border-radius: 0.375rem;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`

const RowHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  cursor: pointer;
`

const DriverList = styled.ul`
  margin: 0;
  padding: 0 0 0 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`

const DriverItem = styled.li`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Name = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Detail = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ActiveControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
`

const MethodSelect = styled.select`
  flex: 1;
  min-width: 0;
  font-size: 0.75rem;
  padding: 0.25rem;
  border-radius: 0.25rem;
  border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
  background: ${({ theme }) => theme.forecast.cardBackground};
  color: ${({ theme }) => theme.forecast.activeText};
`

const StatusNote = styled.span<{ $isError?: boolean }>`
  font-size: 0.6875rem;
  color: ${({ theme, $isError }) =>
    $isError
      ? (theme.forecast.danger ?? '#f92f06')
      : theme.forecast.secondaryText};
`

const EmptyState = styled.div`
  padding: 1rem 0.5rem;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  text-align: center;
`

const spin = keyframes`
  to { transform: rotate(360deg); }
`

const Spinner = styled(Loader2)`
  width: 0.875rem;
  height: 0.875rem;
  animation: ${spin} 1s linear infinite;
`

const shortTagId = (id: string) => id.split('-').slice(1).join('-') || id

const SentinelDriversPanel: React.FC<{
  sentinel: SentinelSummary
  tagLabelById: Map<string, string>
  selectedMethod?: string
}> = ({ sentinel, tagLabelById, selectedMethod }) => {
  const { drivers, methodStats, error, isLoading } = useSentinelDrivers(
    sentinel.id,
    sentinel.resultPath
  )

  if (isLoading) {
    return (
      <StatusNote>
        <Spinner /> Loading drivers…
      </StatusNote>
    )
  }
  if (error) {
    return <StatusNote $isError>Failed to load drivers.</StatusNote>
  }

  const stats = selectedMethod ? methodStats?.[selectedMethod] : undefined

  return (
    <>
      {stats && (
        <StatusNote title='Holdout quality of the selected model method: scaled mean absolute error (lower is better), residual drift, and typical residual size'>
          Model quality: MAE {stats.scaledMae?.toFixed(2) ?? '–'}
          {stats.drift !== null && <> · drift {stats.drift.toFixed(3)}</>}
          {stats.errScale !== null && (
            <> · err scale {stats.errScale.toFixed(3)}</>
          )}
        </StatusNote>
      )}
      {drivers?.length ? (
        <>
          <StatusNote>Drivers</StatusNote>
          <DriverList>
            {drivers.map((driver) => (
              <DriverItem key={driver} title={driver}>
                {tagLabelById.get(driver) ?? shortTagId(driver)}
              </DriverItem>
            ))}
          </DriverList>
        </>
      ) : (
        <StatusNote>No drivers recorded for this sentinel.</StatusNote>
      )}
    </>
  )
}

export const SentinelsTab: React.FC = () => {
  const {
    siteID,
    timeRange,
    dataLayers,
    activeSentinels,
    addSentinel,
    removeSentinel,
    setSentinelMethod,
    sensorEntries,
  } = useScatterplotContext()
  const [search, setSearch] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  // Human-readable labels for driver tag ids, from the site datapoint pool.
  const tagLabelById = useMemo(() => {
    const labels = new Map<string, string>()
    for (const entry of sensorEntries) {
      const data = entry.data as { pi_tag?: string; name?: string }
      labels.set(entry.id, data.pi_tag || data.name || shortTagId(entry.id))
    }
    return labels
  }, [sensorEntries])

  const { sentinels, isLoading: isLoadingList } = useSentinelList(siteID)
  const sentinelsEnabled =
    dataLayers.find((layer) => layer.id === 'sentinels')?.enabled ?? false
  const { scores, scoreWindow } = useSentinelScores(
    siteID,
    activeSentinels,
    timeRange,
    { enabled: sentinelsEnabled }
  )

  // Only target-mode sentinels that finished training can band a pen.
  const listable = useMemo(
    () =>
      sentinels.filter(
        (sentinel) =>
          sentinel.status === 'done' &&
          sentinel.mode === 'target' &&
          sentinel.target
      ),
    [sentinels]
  )

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return listable
    return listable.filter(
      (sentinel) =>
        sentinel.name.toLowerCase().includes(query) ||
        (sentinel.target ?? '').toLowerCase().includes(query)
    )
  }, [listable, search])

  const activeById = useMemo(
    () =>
      new Map(activeSentinels.map((sentinel) => [sentinel.entryId, sentinel])),
    [activeSentinels]
  )

  if (isLoadingList) {
    return (
      <Container>
        <EmptyState>
          <Spinner /> Loading sentinels…
        </EmptyState>
      </Container>
    )
  }

  if (listable.length === 0) {
    return (
      <Container>
        <EmptyState>No sentinels for this site.</EmptyState>
      </Container>
    )
  }

  return (
    <Container>
      <SearchContainer>
        <SearchBar
          placeholder='Search sentinels by name or target tag...'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: '100%' }}
        />
      </SearchContainer>
      <List>
        {filtered.map((sentinel) => {
          const active = activeById.get(sentinel.id)
          const score = active ? scores[sentinel.id] : undefined
          const methodOptions =
            score?.ok && score.response.methods
              ? Object.keys(score.response.methods)
              : [...TARGET_METHOD_KEYS]
          const selectedMethod = active?.method ?? methodOptions[0]

          const expanded = expandedId === sentinel.id

          // Live fit over the scored window: share of buckets out of band
          // and the worst deviation, from the chosen method's sigmas.
          let inViewNote: string | null = null
          if (score?.ok && score.response.methods?.[selectedMethod]) {
            const method = score.response.methods[selectedMethod]
            const targetKey = score.response.target ?? sentinel.target ?? ''
            const residual = method.residual[targetKey] ?? []
            const errScale = method.err_scale[targetKey]
            if (errScale) {
              let scored = 0
              let outOfBand = 0
              let worst = 0
              for (const r of residual) {
                if (r === null || r === undefined) continue
                scored += 1
                const sigma = Math.abs(r) / errScale
                if (sigma >= 2) outOfBand += 1
                if (sigma > worst) worst = sigma
              }
              if (scored > 0) {
                inViewNote = `${((outOfBand / scored) * 100).toFixed(1)}% out of band · worst ${worst.toFixed(1)}σ`
              }
            }
          }

          return (
            <Row key={sentinel.id}>
              <RowHeader
                title='Show drivers'
                onClick={() => setExpandedId(expanded ? null : sentinel.id)}>
                <Name title={sentinel.name}>{sentinel.name}</Name>
                {active ? (
                  <CantileverButton
                    iconOnly
                    ghost
                    size='small'
                    title='Remove sentinel from chart'
                    onClick={(e) => {
                      e.stopPropagation()
                      removeSentinel(sentinel.id)
                    }}>
                    <X size={14} />
                  </CantileverButton>
                ) : (
                  <CantileverButton
                    size='small'
                    onClick={(e) => {
                      e.stopPropagation()
                      if (sentinel.target)
                        addSentinel(sentinel.id, sentinel.target, sentinel.name)
                    }}>
                    Add
                  </CantileverButton>
                )}
              </RowHeader>
              <Detail title={sentinel.detail}>{sentinel.detail}</Detail>
              {expanded && (
                <SentinelDriversPanel
                  sentinel={sentinel}
                  tagLabelById={tagLabelById}
                  selectedMethod={selectedMethod}
                />
              )}
              {active && (
                <>
                  {inViewNote && (
                    <StatusNote title='Over the scored window, with the selected method'>
                      {inViewNote}
                    </StatusNote>
                  )}
                  <StatusNote title='The band and its sigmas are calibrated on interval averages, so raw spikes briefer than the interval are not scored against it'>
                    Band compares{' '}
                    {score?.ok
                      ? (score.response.window?.interval_minutes ?? 10)
                      : 10}
                    -minute averages against the model.
                  </StatusNote>
                  <ActiveControls>
                    <MethodSelect
                      value={selectedMethod}
                      onChange={(e) =>
                        setSentinelMethod(sentinel.id, e.target.value)
                      }>
                      {methodOptions.map((method) => (
                        <option key={method} value={method}>
                          {METHOD_LABELS[method] ?? method}
                        </option>
                      ))}
                    </MethodSelect>
                  </ActiveControls>
                  {score && !score.ok && (
                    <StatusNote $isError>
                      Scoring failed: {score.error}
                    </StatusNote>
                  )}
                  {score?.ok && scoreWindow.clamped && (
                    <StatusNote>
                      Alerting range covers {scoreWindow.startDate} →{' '}
                      {scoreWindow.endDate} (scoring is limited to a 14-day
                      window).
                    </StatusNote>
                  )}
                  {!sentinelsEnabled && (
                    <StatusNote>
                      The Sentinels data layer is off — enable it to see the
                      alerting range on the chart.
                    </StatusNote>
                  )}
                </>
              )}
            </Row>
          )
        })}
        {filtered.length === 0 && (
          <EmptyState>No sentinels match “{search}”.</EmptyState>
        )}
      </List>
    </Container>
  )
}
